import { WorkPage, WorkEmpty } from '../components/work-page.tsx';
import { TableScroll } from '@web/components/table-scroll.tsx';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { quoteApi, quoteRequestApi } from '../api/endpoints.ts';
import { useSession } from '../state/session.tsx';
import { usePlanFeature } from '../components/plan-gate.tsx';
import { Alert, Button, LoadingBlock, Pagination, Select, StatusBadge, TextInput } from '../components/ui.tsx';
import { QuotesTabs } from '../components/quote/quotes-tabs.tsx';
import { BOOKED_FILTER, BookedJobsList } from '../components/quote/booked-jobs.tsx';
import { quoteAmount } from '../components/quote/plain-price.tsx';
import { QuoteListActions } from '../components/quote/list-actions.tsx';
import { DeleteQuoteDialog } from '../components/quote/delete-dialog.tsx';
import { invalidateQuoteViews } from '../state/quote-cache.ts';
import type { Paginated, QuoteSummary } from '@shared/types/index.ts';
import AddressLink from '../components/address-link.tsx';
import { FounderContactLine } from '../components/founder-contact.tsx';
import { useDocumentTitle } from '../hooks/index.ts';
import { formatMinutes } from '../i18n/format.ts';
import { QUOTE_DISPLAY_STATUSES, type SeriesFrequency } from '@shared/constants/index.ts';
import './quotes.css';
import { formatNumericDate } from '../i18n/datetime.ts';

/**
 * Pseudo-value for the status select: not a stored status but the "chase
 * these before they lapse" view — open quotes expiring within a week.
 */
const EXPIRING_FILTER = '__expiring';

/**
 * Whether the quote table fits.
 *
 * Below 1280px the action buttons (email again, copy, book) do not fit
 * beside the quote, so each quote is a card. Missing `matchMedia` (jsdom)
 * is treated as wide so the table the tests read is the one that renders.
 * A phone always has `matchMedia`.
 */
function useWideQuoteList(): boolean {
  const query = '(min-width: 1280px)';
  const [wide, setWide] = useState(() => window.matchMedia?.(query)?.matches ?? true);
  useEffect(() => {
    const list = window.matchMedia?.(query);
    if (!list) return;
    setWide(list.matches);
    const onChange = (event: MediaQueryListEvent) => setWide(event.matches);
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, []);
  return wide;
}

export default function QuotesPage() {
  const { t } = useTranslation();
  useDocumentTitle(t('quotes.list.documentTitle'));
  const { can } = useSession();
  const hasOnlineIntake = usePlanFeature('online_intake');
  // Every plan books jobs from quotes now, so every plan has a Booked view.
  const hasSingleJob = usePlanFeature('single_job');
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const wideList = useWideQuoteList();

  /**
   * What the page someone came from wants said here — today, the quote builder
   * finishing a quote (spec §16.1). Finishing ends on this list, so the
   * confirmation has to survive the navigation or the work looks like it went
   * nowhere.
   *
   * Read once into state and then cleared from the history entry, so a reload
   * or a later Back does not replay a message about something that happened
   * minutes ago.
   */
  const [notice, setNotice] = useState<string | null>(
    () => (location.state as { notice?: string } | null)?.notice ?? null,
  );
  const [deleting, setDeleting] = useState<QuoteSummary | null>(null);
  const deletionNotice = useRef<HTMLDivElement>(null);
  const [deletedId, setDeletedId] = useState<string | null>(null);

  // Back/Forward and same-route filter changes discard an unanswered question.
  useEffect(() => setDeleting(null), [location.key]);
  useEffect(() => {
    if (deletedId) deletionNotice.current?.focus();
  }, [deletedId]);

  function quoteDeleted(quote: QuoteSummary) {
    // Remove the confirmed row even if the refresh fails. The list's existing
    // error/Retry UI still reports a failed refresh instead of claiming empty.
    queryClient.setQueriesData<Paginated<QuoteSummary>>({ queryKey: ['quotes'] }, (current) => {
      if (!current?.items.some((item) => item.id === quote.id)) return current;
      const total = Math.max(0, current.total - 1);
      return {
        ...current,
        items: current.items.filter((item) => item.id !== quote.id),
        total,
        totalPages: Math.max(1, Math.ceil(total / current.pageSize)),
      };
    });
    setSelectedIds((current) => {
      const next = new Set(current);
      next.delete(quote.id);
      return next;
    });
    setDeleting(null);
    setNotice(t('quotes.delete.success', { number: quote.quoteNumber }));
    setDeletedId(quote.id);
    invalidateQuoteViews(queryClient);
  }

  useEffect(() => {
    if ((location.state as { notice?: string } | null)?.notice) {
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }
  }, [location, navigate]);

  const STATUS_OPTIONS = [
    { value: '', label: t('quotes.list.statusAll') },
    // Not a status: the jobs with a visit still to happen, by date.
    ...(hasSingleJob ? [{ value: BOOKED_FILTER, label: t('quotes.list.statusBookedJobs') }] : []),
    { value: EXPIRING_FILTER, label: t('quotes.list.statusExpiringSoon') },
    { value: 'draft,ready', label: t('quotes.list.statusInProgress') },
    { value: 'sent,viewed', label: t('quotes.list.statusAwaitingReply') },
  ];

  const SORT_OPTIONS = [
    { value: 'updated_desc', label: t('quotes.list.sortUpdatedDesc') },
    { value: 'created_desc', label: t('quotes.list.sortCreatedDesc') },
    { value: 'number_desc', label: t('quotes.list.sortNumberDesc') },
    { value: 'total_desc', label: t('quotes.list.sortTotalDesc') },
  ];

  // The URL describes the list, including after Back from a quote or a
  // dashboard link. Local copies only remembered the first status link and
  // lost the search/page when the editor unmounted this page.
  const search = params.get('q') ?? '';
  const status =
    params.get('filter') === 'expiring'
      ? EXPIRING_FILTER
      : params.get('filter') === 'booked'
        ? BOOKED_FILTER
        : (params.get('status') ?? '');
  const bookedView = status === BOOKED_FILTER;
  // Opened from the sales dashboard's "Mine" figures: only quotes this person
  // wrote, so the list holds exactly what the count counted.
  const mine = params.get('mine') === '1';
  const sort = SORT_OPTIONS.some((option) => option.value === params.get('sort'))
    ? params.get('sort')!
    : 'updated_desc';
  const requestedPage = Number(params.get('page') ?? 1);
  const page = Number.isSafeInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkResult, setBulkResult] = useState<{ cancelled: number; skipped: number } | null>(null);

  function changeView(changes: Record<string, string | null>, replace = false) {
    const next = new URLSearchParams(params);
    next.delete('page');
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setParams(next, { replace });
  }

  function setStatus(value: string) {
    changeView({
      status: value === EXPIRING_FILTER || value === BOOKED_FILTER ? null : value,
      filter: value === EXPIRING_FILTER ? 'expiring' : value === BOOKED_FILTER ? 'booked' : null,
    });
  }

  function setPage(value: number) {
    // The pager also corrects pages that disappeared after the list shrank.
    // Replace that invalid entry so Back can leave; user paging still pushes.
    const totalPages = query.isPlaceholderData ? undefined : query.data?.totalPages;
    const correctingPage = totalPages !== undefined && page > totalPages && value === totalPages;
    changeView({ page: value === 1 ? null : String(value) }, correctingPage);
  }

  // A bulk action belongs to the rows staff checked. Never carry those ids
  // into another filter/page, where the selected quotes cannot be reviewed.
  useEffect(() => {
    setSelectedIds(new Set());
    setBulkResult(null);
  }, [search, status, sort, page, mine]);

  const query = useQuery({
    queryKey: ['quotes', { search, status, sort, page, mine }],
    queryFn: () =>
      quoteApi.list({
        q: search,
        status: status === EXPIRING_FILTER ? undefined : status,
        ...(status === EXPIRING_FILTER ? { expiringWithinDays: 7 } : {}),
        ...(mine ? { mine: '1' as const } : {}),
        sort,
        page,
        pageSize: 25,
      }),
    // Keep previous rows visible while the replacement view loads. Its old
    // page count cannot constrain the requested view's URL (see the pager).
    placeholderData: keepPreviousData,
    // The Booked view reads its own list.
    enabled: !bookedView,
  });

  // The lead inbox's unreviewed count, surfaced on the page staff already
  // live on so a request never sits unnoticed.
  const newRequests = useQuery({
    queryKey: ['quote-requests-new-count'],
    queryFn: () => quoteRequestApi.newCount(),
    // Lead capture is a plan feature; without it the endpoint answers 402 and
    // there is no inbox for the badge to count.
    enabled: hasOnlineIntake,
  });

  const bulkCancel = useMutation({
    mutationFn: (ids: string[]) => quoteApi.bulkCancel(ids),
    onSuccess: (result) => {
      setBulkResult({ cancelled: result.cancelledIds.length, skipped: result.skipped.length });
      setSelectedIds(new Set());
      void queryClient.invalidateQueries({ queryKey: ['quotes'] });
    },
  });

  function toggleSelected(id: string) {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Each quote is shown in the currency it was written in, which is what the
  // snapshot on the row is for — reading the company's current setting would
  // restate quotes already sent. A monthly quote's total buys a month, so the
  // row owes the reader "/mo" in the same glance as the figure — and a quote
  // accepted at a recurring frequency owes them "/visit, weekly" the same
  // way: the stored total IS that cadence's price, and four accepted deals
  // that read identically would hide which one is standing work.
  // A quote sent as a price range reads as its band, not the band's middle.
  const quoteMoney = (
    quote: Parameters<typeof quoteAmount>[0] & {
      billingBasis: string;
      acceptedFrequency: SeriesFrequency | null;
    },
  ) => {
    const amount = quoteAmount(quote, t);
    if (quote.billingBasis === 'monthly') return t('quotes.list.perMonth', { amount });
    // An accepted one-time choice needs no suffix: a bare amount already
    // means one visit.
    if (quote.acceptedFrequency && quote.acceptedFrequency !== 'one_time') {
      return t('quotes.list.perVisitFrequency', {
        amount,
        frequency: t(`schedule.frequency.${quote.acceptedFrequency}`),
      });
    }
    return amount;
  };

  const canBulkCancel = can('quotes:write');
  const filtered = Boolean(search || status || mine);
  const visibleSelectedIds = query.isPlaceholderData
    ? []
    : (query.data?.items.filter((quote) => selectedIds.has(quote.id)).map((quote) => quote.id) ?? []);

  function clearFilters() {
    setSelectedIds(new Set());
    changeView({ q: null, status: null, filter: null, sort: null, mine: null });
  }

  return (
    <WorkPage
      title={t('quotes.list.title')}
      description={t('quotes.list.description')}
      actions={
        <>
          {hasOnlineIntake && (newRequests.data?.count ?? 0) > 0 ? (
            <Link to="/requests" className="btn btn-secondary">
              {t('quotes.list.newRequests', { count: newRequests.data?.count ?? 0 })}
            </Link>
          ) : null}
          {can('quotes:write') ? (
            <Link to="/quotes/new" className="btn btn-primary">
              {t('quotes.list.newQuote')}
            </Link>
          ) : null}
        </>
      }
    >
      <QuotesTabs />

      {notice ? (
        <div ref={deletionNotice} tabIndex={-1} className="mb-4">
          <Alert tone="success">{notice}</Alert>
        </div>
      ) : null}

      {bulkResult ? (
        <div className="mb-4">
          <Alert tone={bulkResult.skipped > 0 ? 'warning' : 'success'} title={t('quotes.list.bulkResultTitle')}>
            <p>
              {t('quotes.list.bulkResultCanceled', { count: bulkResult.cancelled })}
              {bulkResult.skipped > 0 ? ` ${t('quotes.list.bulkResultSkipped', { count: bulkResult.skipped })}` : ''}
            </p>
          </Alert>
        </div>
      ) : null}
      {bulkCancel.isError ? (
        <div className="mb-4">
          <Alert tone="error" title={t('quotes.list.bulkErrorTitle')}>
            {(bulkCancel.error as Error).message}
          </Alert>
        </div>
      ) : null}

      <form
        className="card mb-6 grid gap-x-4 sm:grid-cols-3"
        onSubmit={(event) => {
          event.preventDefault();
          setPage(1);
        }}
        role="search"
      >
        {bookedView ? null : (
          <TextInput
            label={t('quotes.list.searchLabel')}
            type="search"
            placeholder={t('quotes.list.searchPlaceholder')}
            value={search}
            onChange={(event) => {
              // Typing should not add one browser-history entry per letter.
              changeView({ q: event.target.value }, true);
            }}
          />
        )}
        <Select
          label={t('quotes.list.statusLabel')}
          options={[
            ...STATUS_OPTIONS,
            ...(!STATUS_OPTIONS.some((option) => option.value === status)
              ? [{ value: status, label: t(`quotes.status.${status}`) }]
              : []),
          ]}
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
          }}
        />
        <details className="self-start">
          <summary className="cursor-pointer py-2 font-semibold">{t('quotes.list.moreFilters')}</summary>
          <Select
            label={t('quotes.list.exactStatus')}
            value={status}
            options={[
              ...STATUS_OPTIONS,
              ...QUOTE_DISPLAY_STATUSES.map((value) => ({ value, label: t(`quotes.status.${value}`) })),
            ]}
            onChange={(event) => {
              setStatus(event.target.value);
            }}
          />
          {!bookedView ? (
            <Select
              label={t('quotes.list.sortLabel')}
              options={SORT_OPTIONS}
              value={sort}
              onChange={(event) => {
                changeView({ sort: event.target.value === 'updated_desc' ? null : event.target.value });
              }}
            />
          ) : null}
        </details>
        {!bookedView && (filtered || sort !== 'updated_desc') ? (
          <div className="sm:col-span-3">
            <Button variant="ghost" onClick={clearFilters}>
              {t('quotes.list.clearFilters')}
            </Button>
          </div>
        ) : null}
      </form>

      {mine && !bookedView ? (
        <p className="mb-4 flex flex-wrap items-center gap-2 text-sm text-[color:var(--color-muted)]">
          {t('quotes.list.mineOnly')}
          <Button variant="ghost" onClick={() => changeView({ mine: null })}>
            {t('quotes.list.showEveryone')}
          </Button>
        </p>
      ) : null}

      {bookedView ? <BookedJobsList /> : null}

      {!bookedView && canBulkCancel && visibleSelectedIds.length > 0 ? (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-[color:var(--color-line)] bg-white p-3">
          <span className="text-sm font-medium">
            {t('quotes.list.selectedCount', { count: visibleSelectedIds.length })}
          </span>
          <Button
            variant="danger"
            busy={bulkCancel.isPending}
            onClick={() => {
              if (window.confirm(t('quotes.list.bulkCancelConfirm', { count: visibleSelectedIds.length }))) {
                bulkCancel.mutate(visibleSelectedIds);
              }
            }}
          >
            {t('quotes.list.bulkCancel')}
          </Button>
          <Button variant="ghost" onClick={() => setSelectedIds(new Set())}>
            {t('quotes.list.clearSelection')}
          </Button>
        </div>
      ) : null}

      {!bookedView && query.isLoading ? <LoadingBlock label={t('quotes.list.loading')} /> : null}

      {!bookedView && query.isError ? (
        <Alert tone="error" title={t('quotes.list.loadErrorTitle')}>
          <p>{(query.error as Error).message}</p>
          <Button
            className="quote-read-retry mt-3"
            busy={query.isFetching}
            onClick={() => void query.refetch({ cancelRefetch: false })}
          >
            {t('common.retry')}
          </Button>
        </Alert>
      ) : null}

      {!bookedView && !query.isError && !query.isPlaceholderData && query.data && query.data.items.length === 0 ? (
        <WorkEmpty title={t(filtered ? 'quotes.list.emptyFilteredTitle' : 'quotes.list.emptyTitle')}>
          <p>{search || status ? t('quotes.list.emptyFiltered') : t('quotes.list.emptyDefault')}</p>
          {/* Only the first-run empty state. A filtered "nothing matched"
              is a search result, not a new account asking for help. */}
          {search || status ? null : <FounderContactLine className="mt-3 text-sm text-[color:var(--color-muted)]" />}
        </WorkEmpty>
      ) : null}

      {!bookedView && query.data && query.data.items.length > 0 ? (
        wideList ? (
          <TableScroll
            aria-label={t('quotes.list.caption', { count: query.data.total })}
            className="overflow-x-auto rounded-lg border border-[color:var(--color-line)] bg-white"
          >
            {/* Identity columns keep their useful width; the whole table scrolls
                if translated actions need more room. Phones use the cards below. */}
            <table className="data-table quotes-table w-full min-w-max border-collapse text-left">
              <caption className="sr-only">{t('quotes.list.caption', { count: query.data.total })}</caption>
              <thead>
                <tr className="border-b border-[color:var(--color-line)] bg-[color:var(--color-surface)]">
                  {canBulkCancel ? (
                    /* w-10 is 40px and px-4 takes 32px of it, so the checkbox
                       was wider than the cell and the cell scroller appeared. */
                    <th scope="col" className="w-14 px-4 py-3">
                      <span className="sr-only">{t('quotes.list.columnSelect')}</span>
                    </th>
                  ) : null}
                  <th scope="col" className="px-4 py-3 text-sm font-semibold whitespace-nowrap">
                    {t('quotes.list.columnQuote')}
                  </th>
                  <th scope="col" className="px-4 py-3 text-sm font-semibold whitespace-nowrap">
                    {t('quotes.list.columnCustomer')}
                  </th>
                  <th scope="col" className="px-4 py-3 text-sm font-semibold whitespace-nowrap">
                    {t('quotes.list.columnStatus')}
                  </th>
                  <th scope="col" className="px-4 py-3 text-right text-sm font-semibold whitespace-nowrap">
                    {t('quotes.list.columnTime')}
                  </th>
                  <th scope="col" className="px-4 py-3 text-right text-sm font-semibold whitespace-nowrap">
                    {t('quotes.list.columnTotal')}
                  </th>
                  <th scope="col" className="px-4 py-3 text-right text-sm font-semibold">
                    <span className="sr-only">{t('quotes.list.columnActions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {query.data.items.map((quote) => (
                  <tr
                    key={quote.id}
                    data-selected={selectedIds.has(quote.id)}
                    className="border-b border-[color:var(--color-line)] last:border-0"
                  >
                    {canBulkCancel ? (
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-[color:var(--color-line)]"
                          aria-label={t('quotes.list.selectQuote', { number: quote.quoteNumber })}
                          checked={selectedIds.has(quote.id)}
                          disabled={query.isPlaceholderData}
                          onChange={() => toggleSelected(quote.id)}
                        />
                      </td>
                    ) : null}
                    <td className="px-4 py-3">
                      <Link
                        to={`/quotes/${quote.id}`}
                        className="whitespace-nowrap font-semibold text-[color:var(--color-brand)] hover:underline"
                      >
                        {quote.quoteNumber}
                      </Link>
                      <div className="text-sm text-[color:var(--color-muted)]">
                        {formatNumericDate(quote.quoteDate)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {/* Bounded text wraps fully, including unbroken names. */}
                      <div className="table-text min-w-[12rem] max-w-[18rem]">{quote.customerNameSnapshot}</div>
                      {quote.propertyAddressSnapshot ? (
                        <div className="max-w-[18rem] table-text text-sm text-[color:var(--color-muted)]">
                          {/* The row shows the first line and the link searches the
                            whole address: a street with no town is a search that
                            matches hundreds of them. Bounded, because this is the
                            one cell a customer can make as wide as they like. */}
                          <AddressLink address={quote.propertyAddressSnapshot}>
                            {quote.propertyAddressSnapshot.split('\n')[0]}
                          </AddressLink>
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={quote.status} expired={quote.isExpired} quote={quote} />
                    </td>
                    <td className="tabular px-4 py-3 text-right text-sm whitespace-nowrap">
                      {formatMinutes(quote.estimatedMinutes)}
                    </td>
                    <td className="tabular px-4 py-3 text-right font-semibold whitespace-nowrap">
                      {quoteMoney(quote)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <QuoteListActions quote={quote} onDelete={setDeleting} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        ) : (
          <ul className="flex flex-col gap-3">
            {query.data.items.map((quote) => (
              <li
                key={quote.id}
                data-selected={selectedIds.has(quote.id)}
                className="quote-list-card rounded-lg border border-[color:var(--color-line)] bg-white p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  {canBulkCancel ? (
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-[color:var(--color-line)]"
                      aria-label={t('quotes.list.selectQuote', { number: quote.quoteNumber })}
                      checked={selectedIds.has(quote.id)}
                      disabled={query.isPlaceholderData}
                      onChange={() => toggleSelected(quote.id)}
                    />
                  ) : null}
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/quotes/${quote.id}`}
                      className="whitespace-nowrap font-semibold text-[color:var(--color-brand)] hover:underline"
                    >
                      {quote.quoteNumber}
                    </Link>
                    <div className="text-sm text-[color:var(--color-muted)]">{formatNumericDate(quote.quoteDate)}</div>
                    <div className="table-text mt-2">{quote.customerNameSnapshot}</div>
                    {quote.propertyAddressSnapshot ? (
                      <div className="table-text text-sm text-[color:var(--color-muted)]">
                        <AddressLink address={quote.propertyAddressSnapshot}>
                          {quote.propertyAddressSnapshot.split('\n')[0]}
                        </AddressLink>
                      </div>
                    ) : null}
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                      <StatusBadge status={quote.status} expired={quote.isExpired} quote={quote} />
                      <span className="tabular text-[color:var(--color-muted)]">
                        {formatMinutes(quote.estimatedMinutes)}
                      </span>
                      <span className="tabular font-semibold">{quoteMoney(quote)}</span>
                    </div>
                  </div>
                </div>
                <QuoteListActions quote={quote} className="mt-3" onDelete={setDeleting} />
              </li>
            ))}
          </ul>
        )
      ) : null}

      {!bookedView ? (
        <div className="quote-list-pagination">
          <Pagination
            page={page}
            totalPages={query.isPlaceholderData ? undefined : query.data?.totalPages}
            onPageChange={setPage}
          />
        </div>
      ) : null}
      {deleting ? (
        <DeleteQuoteDialog
          key={deleting.id}
          quote={deleting}
          onCancel={() => setDeleting(null)}
          onDeleted={quoteDeleted}
        />
      ) : null}
    </WorkPage>
  );
}
